<template>
  <div
    class="filter-dropdown"
    :class="[`variant-${variant}`]"
    ref="dropdownRef"
  >
    <label v-if="singleCheckbox" class="single-checkbox-row">
      <span class="filter-label">{{ label }}</span>
      <span class="single-checkbox-control">
        <input
          type="checkbox"
          :checked="hasSelections"
          :disabled="options[0]?.disabled"
          @change="toggleSelection(options[0])"
        />
        <span class="single-checkbox-visual" aria-hidden="true"></span>
      </span>
    </label>

    <!-- WCAG 4.1.2: button provides keyboard access + aria-expanded state -->
    <button
      v-else
      type="button"
      class="filter-trigger"
      :class="{ active: isOpen || hasSelections }"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      :aria-controls="dropdownId + '-panel'"
      :id="dropdownId + '-trigger'"
      @mouseenter="showOptions"
      @mouseleave="hideOptions"
      @click="toggleOptions"
    >
      <div class="trigger-content">
        <span class="filter-label">{{ label }}</span>
      </div>
      <!-- Decorative chevron SVG hidden from AT (WCAG 1.1.1) -->
      <div class="filter-arrow" aria-hidden="true">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M9 6L15 12L9 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </button>

    <Transition name="dropdown">
      <div
        v-if="!singleCheckbox && isOpen"
        :id="dropdownId + '-panel'"
        class="options-panel"
        role="listbox"
        :aria-labelledby="dropdownId + '-trigger'"
        aria-multiselectable="true"
        @mouseenter="keepOpen"
        @mouseleave="hideOptions"
      >
        <div class="options-container">
          <div
            v-for="option in options"
            :key="getOptionValue(option)"
            class="option-item"
            role="option"
            :aria-selected="isSelected(getOptionValue(option))"
            :aria-disabled="option.disabled || undefined"
            :tabindex="option.disabled ? -1 : 0"
            :class="{
              selected: isSelected(getOptionValue(option)),
              disabled: option.disabled,
            }"
            @click="toggleSelection(option)"
            @keydown.space.prevent="toggleSelection(option)"
            @keydown.enter.prevent="toggleSelection(option)"
          >
            <div class="option-content">
              <span class="option-text">{{ getOptionText(option) }}</span>
              <!-- Checkbox is aria-hidden; the parent [role=option] communicates state (WCAG 4.1.2) -->
              <label class="checkbox-wrapper" aria-hidden="true" @click.stop>
                <input
                  type="checkbox"
                  :checked="isSelected(getOptionValue(option))"
                  :disabled="option.disabled"
                  tabindex="-1"
                  @change="toggleSelection(option)"
                />
                <span class="checkbox-visual"></span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from "vue";

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  options: {
    type: Array,
    default: () => [],
  },
  label: {
    type: String,
    required: true,
  },
  variant: {
    type: String,
    default: "green",
  },
  singleCheckbox: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "change", "clear"]);

const dropdownRef = ref(null);
const isOpen = ref(false);
let hoverTimeout = null;

// Unique ID for aria-controls/aria-labelledby pairing (WCAG 4.1.2)
const dropdownId = `filter-dropdown-${Math.random().toString(36).slice(2, 8)}`;

const selectedItems = computed(() => props.modelValue || []);
const hasSelections = computed(() => selectedItems.value.length > 0);

// Helper functions to handle different option formats
const getOptionValue = (option) => {
  return option?.value !== undefined ? option.value : option;
};

const getOptionText = (option) => {
  return option?.text || option?.title || option?.label || String(option);
};

const isSelected = (value) => {
  // Normalize values to primitives for comparison
  const selectedValues = (selectedItems.value || []).map((v) =>
    v?.value !== undefined ? v.value : v
  );
  return selectedValues.includes(value);
};

const showOptions = () => {
  clearTimeout(hoverTimeout);
  isOpen.value = true;
};

const hideOptions = () => {
  hoverTimeout = setTimeout(() => {
    isOpen.value = false;
  }, 150); // Small delay to allow moving to options panel
};

const keepOpen = () => {
  clearTimeout(hoverTimeout);
};

const toggleOptions = () => {
  if (isOpen.value) {
    hideOptions();
  } else {
    showOptions();
  }
};

const toggleSelection = (option) => {
  if (option.disabled) return;

  const value = getOptionValue(option);
  const newSelection = [...selectedItems.value];
  const index = newSelection.indexOf(value);

  // Handle "all" option selection
  if (value === "all") {
    if (index === -1) {
      // Select all options
      const allValues = props.options
        .filter((opt) => !opt.disabled)
        .map((opt) => getOptionValue(opt));
      emit("update:modelValue", allValues);
      emit("change", {
        value: allValues,
        option,
        action: "select_all",
      });
    } else {
      // Deselect all options
      emit("update:modelValue", []);
      emit("change", {
        value: [],
        option,
        action: "deselect_all",
      });
    }
    return;
  }

  // Handle individual option selection
  if (index === -1) {
    newSelection.push(value);

    // Check if we now have all non-"all" options selected
    const nonAllOptions = props.options.filter(
      (opt) => getOptionValue(opt) !== "all" && !opt.disabled
    );
    const selectedNonAllOptions = newSelection.filter((val) => val !== "all");

    // If all non-"all" options are selected, add "all" to selection
    if (
      selectedNonAllOptions.length === nonAllOptions.length &&
      !newSelection.includes("all")
    ) {
      newSelection.push("all");
    }
  } else {
    // Remove selection
    newSelection.splice(index, 1);

    // If "all" was selected, remove it when any individual item is deselected
    const allIndex = newSelection.indexOf("all");
    if (allIndex > -1) {
      newSelection.splice(allIndex, 1);
    }
  }

  emit("update:modelValue", newSelection);
  emit("change", {
    value: newSelection,
    option,
    action: index === -1 ? "add" : "remove",
  });
};
</script>

<style scoped>
.filter-dropdown {
  position: relative;
  width: 100%;
  --checkbox-accent: var(--green-dark-1);
}

.single-checkbox-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  width: 100%;
  height: 48px;
  padding: 4px 8px 4px 16px;
  border-radius: 12px;
  cursor: pointer;
}

.single-checkbox-row:hover {
  background-color: #f9fafb;
}

.single-checkbox-row .filter-label {
  color: var(--checkbox-accent);
  line-height: 28px;
}

.single-checkbox-control {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
}

.single-checkbox-control input {
  position: absolute;
  inset: 0;
  margin: 0;
  opacity: 0;
  cursor: pointer;
}

.single-checkbox-visual {
  position: relative;
  width: 24px;
  height: 24px;
  background-color: var(--white);
  border: 1px solid var(--white-inactive);
  border-radius: 4px;
  pointer-events: none;
}

.single-checkbox-control input:focus-visible + .single-checkbox-visual {
  outline: 3px solid var(--checkbox-accent);
  outline-offset: 2px;
}

.single-checkbox-control input:checked + .single-checkbox-visual {
  background-color: var(--checkbox-accent);
  border-color: var(--checkbox-accent);
}

.single-checkbox-control input:checked + .single-checkbox-visual::after {
  content: "";
  position: absolute;
  left: 8px;
  top: 3px;
  width: 6px;
  height: 12px;
  border: solid var(--white);
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.filter-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  /* Reset native <button> styles (WCAG 4.1.2) */
  width: 100%;
  border: none;
  background: none;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  text-align: left;
}

/* Visible focus ring for keyboard users (WCAG 2.4.7) */
.filter-trigger:focus-visible {
  outline: 3px solid var(--primary-green, #00925e);
  outline-offset: 2px;
}

.filter-trigger:hover {
  background-color: var(--primary-green);
}

.filter-trigger.active {
  background-color: var(--primary-green);
}

.filter-trigger:hover .filter-label,
.filter-trigger:hover .filter-arrow,
.filter-trigger.active .filter-label,
.filter-trigger.active .filter-arrow {
  color: #ffffff;
}

.trigger-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.filter-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-big);
  line-height: 24px;
  color: #007a50;
  letter-spacing: 0.024px;
}

.filter-arrow {
  color: #007a50;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.filter-arrow svg {
  width: 18px;
  height: 18px;
}

.options-panel {
  position: absolute;
  top: 0;
  left: calc(100% + 20px);
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 1001;
  min-width: 250px;
  max-width: 300px;
}

.options-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.options-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-small);
  color: #007a50;
}

.clear-button {
  background: none;
  border: none;
  color: #ef4444;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.clear-button:hover {
  background-color: #fee2e2;
}

.options-container {
  max-height: 300px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

.options-container::-webkit-scrollbar {
  width: 6px;
}

.options-container::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.options-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.options-container::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.option-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.option-item:hover:not(.disabled) {
  background-color: #f9fafb;
}

.option-item.selected {
  background-color: #f0f9ff;
  color: #0369a1;
}

.option-item.selected:hover {
  background-color: #e0f2fe;
}

.option-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.option-content {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.checkbox-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.checkbox-wrapper input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.checkbox-visual {
  width: 18px;
  height: 18px;
  background-color: #ffffff;
  border: 2px solid var(--primary-green);
  border-radius: 4px;
  position: relative;
  transition: all 0.2s ease;
  cursor: pointer;
}

.checkbox-wrapper input[type="checkbox"]:checked + .checkbox-visual {
  background-color: var(--primary-green);
  border-color: var(--primary-green);
}

.checkbox-visual:after {
  content: "";
  position: absolute;
  display: none;
}

.checkbox-wrapper input[type="checkbox"]:checked + .checkbox-visual:after {
  display: block;
}

.checkbox-wrapper .checkbox-visual:after {
  left: 5px;
  top: 2px;
  width: 4px;
  height: 8px;
  border: solid #ffffff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.option-text {
  flex: 1;
  font-size: var(--fs-small);
  font-weight: 600;
  color: #007a50;
}

/* Animations */
@keyframes counter-appear {
  from {
    opacity: 0;
    transform: scale(0.8);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Dropdown animation */
.dropdown-enter-active {
  transition: all 0.2s ease;
}

.dropdown-leave-active {
  transition: all 0.15s ease;
}

.dropdown-enter-from {
  opacity: 0;
  transform: translateX(-8px);
}

.dropdown-leave-to {
  opacity: 0;
  transform: translateX(-4px);
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .options-panel {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-width: 90vw;
    max-height: 80vh;
  }

  .filter-trigger {
    padding: 8px 12px;
    height: 40px;
  }

  .filter-label {
    font-size: var(--fs-small);
  }

  .item-counter {
    min-width: 18px;
    height: 18px;
    font-size: 11px;
  }
}

/* Purple Variant Overrides */
.variant-purple {
  --checkbox-accent: var(--primary-purple);
}

.variant-purple .filter-trigger:focus-visible {
  outline-color: var(--primary-purple);
}

.variant-purple .filter-trigger:hover,
.variant-purple .filter-trigger.active {
  background-color: var(--primary-purple);
}

.variant-purple .filter-label,
.variant-purple .filter-arrow,
.variant-purple .options-title,
.variant-purple .option-text {
  color: var(--primary-purple);
}

.variant-purple .checkbox-visual {
  border-color: var(--primary-purple);
}

.variant-purple
  .checkbox-wrapper
  input[type="checkbox"]:checked
  + .checkbox-visual {
  background-color: var(--primary-purple);
  border-color: var(--primary-purple);
}

.variant-purple .option-item.selected {
  background-color: #f2f3fb;
  color: var(--primary-purple);
}

.variant-purple .option-item.selected:hover {
  background-color: #e8e7f5;
}

/* Pink Variant Overrides */
.variant-pink {
  --checkbox-accent: var(--pink-dark-1);
}

.variant-pink .filter-trigger:focus-visible {
  outline-color: var(--pink-dark-1);
}

.variant-pink .filter-trigger:hover,
.variant-pink .filter-trigger.active {
  background-color: var(--pink-dark-1);
}

.variant-pink .filter-label,
.variant-pink .filter-arrow,
.variant-pink .options-title,
.variant-pink .option-text {
  color: var(--pink-dark-1);
}

.variant-pink .checkbox-visual {
  border-color: var(--pink-dark-1);
}

.variant-pink
  .checkbox-wrapper
  input[type="checkbox"]:checked
  + .checkbox-visual {
  background-color: var(--pink-dark-1);
  border-color: var(--pink-dark-1);
}

.variant-pink .option-item.selected {
  background-color: #fce6f0;
  color: var(--pink-dark-1);
}

.variant-pink .option-item.selected:hover {
  background-color: #fbd6e9;
}
</style>
